import { Vehicle } from '../../../../types/vehicle';
import { OperatingPeriod, BreakPeriod } from '../../../../types/simulation';
import { RouteAssignment } from '../../../../types/fixedRoutes';

export type BlockStatus = 'working' | 'break' | 'off';

interface Block {
  status: BlockStatus;
  serviceType?: 'fixed' | 'on-demand' | 'mixed';
  routeAssignments?: RouteAssignment[];
  breakLocation?: string;
}

function parseTime(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function explodeToBlocks(
  vehicle: Vehicle,
  rangeStart: number,
  rangeEnd: number,
  intervalMinutes: number,
): Block[] {
  const blockCount = Math.floor((rangeEnd - rangeStart) / intervalMinutes);
  const blocks: Block[] = Array.from({ length: blockCount }, () => ({ status: 'off' }));
  for (const period of vehicle.operatingHours) {
    const periodStart = parseTime(period.start);
    const periodEnd = parseTime(period.end);
    for (let i = 0; i < blockCount; i++) {
      const blockMinStart = rangeStart + i * intervalMinutes;
      const blockMinEnd = blockMinStart + intervalMinutes;
      if (blockMinStart >= periodStart && blockMinStart < periodEnd) {
        blocks[i] = {
          status: 'working',
          serviceType: period.serviceType,
          routeAssignments: period.routeAssignments,
        };
        const breaks = period.breaks ?? [];
        for (const br of breaks) {
          const brStart = parseTime(br.start);
          const brEnd = parseTime(br.end);
          if (blockMinStart < brEnd && blockMinEnd > brStart) {
            blocks[i] = {
              status: 'break',
              serviceType: period.serviceType,
              routeAssignments: period.routeAssignments,
              breakLocation: br.location,
            };
            break;
          }
        }
      }
    }
  }
  return blocks;
}

function reconstructOperatingHours(
  blocks: Block[],
  rangeStart: number,
  intervalMinutes: number,
): OperatingPeriod[] {
  const periods: OperatingPeriod[] = [];
  let i = 0;
  while (i < blocks.length) {
    if (blocks[i].status === 'off') {
      i++;
      continue;
    }
    const periodStartMin = rangeStart + i * intervalMinutes;
    const seed = blocks[i];
    const breaks: BreakPeriod[] = [];
    let j = i;
    while (j < blocks.length && blocks[j].status !== 'off') {
      if (blocks[j].status === 'break') {
        const breakStartMin = rangeStart + j * intervalMinutes;
        const breakLocation = blocks[j].breakLocation ?? '';
        let k = j;
        while (
          k < blocks.length
          && blocks[k].status === 'break'
          && (blocks[k].breakLocation ?? '') === breakLocation
        ) {
          k++;
        }
        const breakEndMin = rangeStart + k * intervalMinutes;
        breaks.push({
          start: minutesToTime(breakStartMin),
          end: minutesToTime(breakEndMin),
          location: breakLocation,
        });
        j = k;
      } else {
        j++;
      }
    }
    const periodEndMin = rangeStart + j * intervalMinutes;
    const period: OperatingPeriod = {
      start: minutesToTime(periodStartMin),
      end: minutesToTime(periodEndMin),
      breaks,
    };
    if (seed.serviceType !== undefined) period.serviceType = seed.serviceType;
    if (seed.routeAssignments !== undefined) period.routeAssignments = seed.routeAssignments;
    periods.push(period);
    i = j;
  }
  return periods;
}

/**
 * Returns the status of a single block within a vehicle's operatingHours.
 * Exposed for callers that need to render block state without mutating.
 */
export function getBlockStatus(
  vehicle: Vehicle,
  blockStartMinutes: number,
  intervalMinutes: number,
): BlockStatus {
  const blockEndMin = blockStartMinutes + intervalMinutes;
  for (const period of vehicle.operatingHours) {
    const periodStart = parseTime(period.start);
    const periodEnd = parseTime(period.end);
    if (blockStartMinutes >= periodStart && blockStartMinutes < periodEnd) {
      const breaks = period.breaks ?? [];
      for (const br of breaks) {
        const brStart = parseTime(br.start);
        const brEnd = parseTime(br.end);
        if (blockStartMinutes < brEnd && blockEndMin > brStart) {
          return 'break';
        }
      }
      return 'working';
    }
  }
  return 'off';
}

/**
 * Returns a new Vehicle with the block at `blockStartMinutes` toggled to the
 * next status in the cycle: working → break → off → working.
 *
 * Splits and merges OperatingPeriods and BreakPeriods as needed so the
 * resulting operatingHours faithfully reflects the per-block status.
 *
 * Service hours range is derived from the union of existing periods plus the
 * target block, clamped to the [0, 1440] day. Block boundaries are quantized
 * to `intervalMinutes` starting from minute 0.
 */
export function toggleVehicleBlock(
  vehicle: Vehicle,
  blockStartMinutes: number,
  intervalMinutes: number,
): Vehicle {
  const blockEndMin = blockStartMinutes + intervalMinutes;

  // Range must cover all existing periods + the target block. Quantize to grid
  // starting at minute 0 of the day so block indices line up reliably.
  const allStarts = vehicle.operatingHours.map(p => parseTime(p.start));
  const allEnds = vehicle.operatingHours.map(p => parseTime(p.end));
  let rangeStart = Math.min(0, blockStartMinutes, ...allStarts);
  let rangeEnd = Math.max(1440, blockEndMin, ...allEnds);
  // Snap to interval grid (rangeStart already at 0 by clamp above; rangeEnd at 1440)
  rangeStart = Math.floor(rangeStart / intervalMinutes) * intervalMinutes;
  rangeEnd = Math.ceil(rangeEnd / intervalMinutes) * intervalMinutes;

  const blocks = explodeToBlocks(vehicle, rangeStart, rangeEnd, intervalMinutes);
  const blockIdx = Math.floor((blockStartMinutes - rangeStart) / intervalMinutes);
  if (blockIdx < 0 || blockIdx >= blocks.length) return vehicle;

  const current = blocks[blockIdx];
  let next: Block;
  if (current.status === 'working') {
    next = {
      status: 'break',
      serviceType: current.serviceType,
      routeAssignments: current.routeAssignments,
      breakLocation: '',
    };
  } else if (current.status === 'break') {
    next = { status: 'off' };
  } else {
    // off → working. Inherit metadata from an adjacent working block if any.
    const left = blocks[blockIdx - 1];
    const right = blocks[blockIdx + 1];
    const seed = (left && left.status === 'working')
      ? left
      : (right && right.status === 'working' ? right : null);
    next = {
      status: 'working',
      serviceType: seed?.serviceType ?? 'on-demand',
      routeAssignments: seed?.routeAssignments,
    };
  }
  blocks[blockIdx] = next;

  return {
    ...vehicle,
    operatingHours: reconstructOperatingHours(blocks, rangeStart, intervalMinutes),
  };
}
